import { Card, CardContent, cn } from "@exhousing/design-system";
import { useState } from "react";

import { useLedgerAccounts, useLedgerEntries } from "@/hooks/api/useLedger";
import { money } from "@/lib/money";

export function LedgerPage() {
  const { data: accounts, isLoading } = useLedgerAccounts();
  const [selected, setSelected] = useState<string | null>(null);
  const { data: entries } = useLedgerEntries(selected ?? undefined);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">Ledger</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Double-entry chart of accounts. Every balance is the sum of its postings —
          select an account to see the entries behind it.
        </p>
      </header>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3 font-medium">Code</th>
                <th className="px-5 py-3 font-medium">Account</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {accounts?.map((a) => (
                <tr
                  key={a.code}
                  onClick={() => setSelected((s) => (s === a.code ? null : a.code))}
                  className={cn(
                    "cursor-pointer border-b last:border-0 transition-colors hover:bg-muted/40",
                    selected === a.code && "bg-muted/60",
                  )}
                >
                  <td className="px-5 py-3 font-mono text-xs text-muted-foreground">
                    {a.code}
                  </td>
                  <td className="px-5 py-3 font-medium">{a.name}</td>
                  <td className="px-5 py-3 capitalize text-muted-foreground">{a.type}</td>
                  <td
                    className={cn(
                      "px-5 py-3 text-right font-medium tabular-nums",
                      a.balance < 0 && "text-muted-foreground",
                    )}
                  >
                    {money(a.balance)}
                  </td>
                </tr>
              ))}
              {!isLoading && accounts?.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-muted-foreground">
                    No accounts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {selected && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <h2 className="mb-4 text-sm font-medium">
              Journal entries touching{" "}
              <span className="font-mono text-xs">{selected}</span>
            </h2>
            <div className="space-y-3">
              {entries?.length === 0 && (
                <p className="text-sm text-muted-foreground">No entries yet.</p>
              )}
              {entries?.map((e) => (
                <div key={e.id} className="rounded-lg border">
                  <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
                    <span>{e.entry_date}</span>
                    <span>{e.memo}</span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {e.postings.map((p, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-4 py-1.5">
                            <span className="font-mono text-xs text-muted-foreground">
                              {p.account_code}
                            </span>{" "}
                            {p.account_name}
                          </td>
                          <td className="px-4 py-1.5 text-right tabular-nums">
                            {p.side === "debit" ? money(p.amount) : ""}
                          </td>
                          <td className="px-4 py-1.5 text-right tabular-nums">
                            {p.side === "credit" ? money(p.amount) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
